import { Dispatch, SetStateAction } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ISaveSchool } from "../../interfaces/services";
import { ISelectedData } from "../../interfaces/tree";
import { IUserData } from "../../interfaces/forms";

import api from "../apiClient";

import useToast from "../useToast";
import useSaveUsers from "../users/useSaveUsers";

import cookiesHelper from "../../helpers/cookies";

export interface IPayload {
    payload: ISaveSchool;
    usersPayload: IUserData[];
    setIsProcessing: Dispatch<SetStateAction<boolean>>;
    selected: ISelectedData | null;
    setSelected: Dispatch<SetStateAction<ISelectedData | null>>;
};
const useSaveSchool = () => {
    const queryClient = useQueryClient();

    const { addToast } = useToast();

    const { mutate: saveUsers } = useSaveUsers();

    const mutationFn = async (data: IPayload) => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`

        const res = await api.post(`schools`, data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables: IPayload) => {
            variables.setIsProcessing(true);
        },
        onSuccess: (response: any, variables: IPayload) => {
            if (response) {
                if (variables.selected) {
                    variables.setSelected({
                        id: response.id,
                        level: 0,
                        data: {
                            ...variables.selected.data,
                            id: response.id,
                            dli: response.dli,
                            name: response.name,
                            status: response.status,
                            url: response.url,
                            domain: response.domain,
                            logo_asset_name: response.logo_asset_name,
                            support_email: response.support_email,
                        },
                    });
                };

                if (variables.usersPayload?.length) {
                    saveUsers({
                        payload: variables.usersPayload,
                        setIsProcessing: variables.setIsProcessing,
                    });
                };

                queryClient.invalidateQueries({ queryKey: ['schools_tree'] });
                if (variables.payload.id) {
                    queryClient.invalidateQueries({ queryKey: ['users', variables.payload.id] });
                };
            };
        },
        onError: (error: any, variables: IPayload) => {
            addToast({
                type: 'error',
                title: 'Error occured',
                message: error?.response?.data?.message ?? "Something went wrong"
            });
        },
        onSettled: (data: any, error: any, variables: IPayload) => {
            variables.setIsProcessing(false);
        },
    });
};

export default useSaveSchool;